/**
 * @file        src/pages/blog/AddBlog.jsx
 * @description Create / edit a blog post: title, slug, heading, image upload,
 *              category, SEO meta, and a rich-text body. Reused for
 *              /blog/posts/new and /blog/posts/:id/edit.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-09-15 Claude — Language is a locked English-only dropdown reading
 *                          "English", not a free-text box defaulting to "en".
 *                          It accepted any 2-8 character string for a storefront
 *                          that serves English only, so a row saved as hi/mr was
 *                          content nobody could read. Options come from
 *                          lib/languages.js — one list for all five forms.
 *   - 2026-07-01 Claude — Initial (Blog Management).
 *   - 2026-10-09 Claude — The lone "Meta Title" input is folded into the shared
 *                          Search & social block (components/SeoFields.jsx,
 *                          migration 077): meta title/description/keywords,
 *                          share image, canonical override, index toggle and a
 *                          server-resolved search preview. Every SEO key is sent
 *                          on save so clearing one actually clears it.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import ImageUpload from '../../components/ImageUpload';
import RichTextEditor from '../../components/RichTextEditor';
import SeoFields, { SEO_EMPTY, seoFromRow, seoBody, seoErrors, previewSlug } from '../../components/SeoFields';
import { get, post, patch } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { LANGUAGE_OPTIONS, DEFAULT_LANGUAGE } from '../../lib/languages';

const schema = z.object({
  title: z.string().trim().min(1, 'Required').max(220),
  slug: z.string().trim().max(240).optional(),
  heading: z.string().trim().max(255).optional(),
  category_id: z.union([z.coerce.number().int().positive(), z.literal('')]).optional(),
  language: z.enum(['en'], { errorMap: () => ({ message: 'English only' }) }),
  is_active: z.boolean(),
});

const empty = {
  title: '', slug: '', heading: '', image: '', category_id: '',
  language: DEFAULT_LANGUAGE, is_active: true, description: '',
};

export default function AddBlog() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(empty);
  const [seo, setSeo] = useState(SEO_EMPTY);
  const [errors, setErrors] = useState({});

  const { data: cats } = useQuery({
    queryKey: qk.blogCategories.list({ pageSize: 200 }),
    queryFn: () => get('/blog-categories', { params: { pageSize: 200 } }),
  });
  const categories = cats?.data || [];

  const { data: existing } = useQuery({
    queryKey: qk.blogs.one(id),
    queryFn: () => get(`/blogs/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing?.data) {
      const b = existing.data;
      setForm({
        title: b.title || '', slug: b.slug || '', heading: b.heading || '', image: b.image || '',
        category_id: b.category_id || '',
        language: b.language || DEFAULT_LANGUAGE, is_active: !!b.is_active, description: b.description || '',
      });
      setSeo(seoFromRow(b));
    }
  }, [existing]);

  // The draft the server resolves the search preview from.
  const previewItem = useMemo(() => ({
    id: existing?.data?.id,
    title: form.title,
    slug: previewSlug(form.slug || form.title),
    heading: form.heading,
    description: form.description,
    image: form.image,
    created_at: existing?.data?.created_at,
    updated_at: existing?.data?.updated_at,
  }), [form, existing]);

  const mut = useMutation({
    mutationFn: (b) => (isEdit ? patch(`/blogs/${id}`, b) : post('/blogs', b)),
    onSuccess: () => {
      toast.ok(isEdit ? 'Blog updated' : 'Blog added', `${form.title} saved.`);
      qc.invalidateQueries({ queryKey: ['blogs'] });
      navigate('/blog/posts');
    },
    onError: (err) => {
      const f = err.response?.data?.error?.fields;
      if (f) setErrors(f);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    const fe = seoErrors(seo);
    if (!parsed.success) {
      for (const i of parsed.error.issues) fe[i.path.join('.')] = i.message;
    }
    if (!parsed.success || Object.keys(fe).length) return setErrors(fe);
    setErrors({});
    const body = {
      title: parsed.data.title,
      language: parsed.data.language,
      is_active: parsed.data.is_active,
      ...seoBody(seo),
    };
    if (parsed.data.slug) body.slug = parsed.data.slug;
    if (parsed.data.heading) body.heading = parsed.data.heading;
    if (parsed.data.category_id !== '' && parsed.data.category_id != null) body.category_id = Number(parsed.data.category_id);
    if (form.image) body.image = form.image;
    if (form.description) body.description = form.description;
    mut.mutate(body);
  }

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <>
      <PageHead
        title={isEdit ? 'Edit Blog' : 'Add Blog'}
        subtitle="Blog Management"
        actions={<button className="btn ghost" onClick={() => navigate('/blog/posts')}><Icon name="x" /> Cancel</button>}
      />
      <article className="panel glass">
        <form className="form-grid" onSubmit={onSubmit}>
          <Field label="Title" required err={errors.title}>
            <input value={form.title} onChange={on('title')} placeholder="Blog title" />
          </Field>
          <Field label="Slug" err={errors.slug} hint="Auto from title if blank.">
            <input value={form.slug} onChange={on('slug')} placeholder="blog-link" />
          </Field>
          <Field label="Heading" err={errors.heading}>
            <input value={form.heading} onChange={on('heading')} placeholder="Blog heading" />
          </Field>

          <Field label="Category" err={errors.category_id}>
            <Select
              value={form.category_id}
              onChange={(v) => setForm({ ...form, category_id: v })}
              placeholder="— Select Category —"
              options={[{ value: '', label: '— Select Category —' }, ...categories.map((c) => ({ value: c.id, label: c.name }))]}
            />
          </Field>
          <Field label="Language" required err={errors.language}
                 hint="English is the only language the website serves.">
            <Select
              value={form.language}
              onChange={(v) => setForm({ ...form, language: v })}
              options={LANGUAGE_OPTIONS}
            />
          </Field>
          <Field label="Image" full>
            <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} />
          </Field>

          <Field label="Description" full>
            <RichTextEditor value={form.description} onChange={(html) => setForm({ ...form, description: html })} />
          </Field>

          <Field label="Status">
            <label className="check">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              <span>Active (visible on the website)</span>
            </label>
          </Field>

          <SeoFields value={seo} onChange={setSeo} errors={errors} itemKind="blog" item={previewItem} />

          <div className="form-actions">
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="plus" /> {mut.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Submit'}
            </button>
          </div>
        </form>
      </article>
    </>
  );
}

function Field({ label, required, err, hint, full, children }) {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label>{label} {required ? <span className="req">*</span> : null}</label>
      {children}
      {hint && !err ? <div className="field-hint muted">{hint}</div> : null}
      {err ? <div className="field-error">{err}</div> : null}
    </div>
  );
}
